import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import api from '../utils/api';
import { Check, Zap, Crown, Star } from 'lucide-react';

export const Subscription = () => {
  const [plans, setPlans] = useState({});
  const [oneTimeItems, setOneTimeItems] = useState({});
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [loading, setLoading] = useState(false);
  const [activeSubscription, setActiveSubscription] = useState(null);

  useEffect(() => {
    fetchPlans();
    fetchOneTimeItems();
    fetchActiveSubscription();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await api.get('/subscriptions/plans');
      setPlans(response.data);
    } catch (error) {
      toast.error('Failed to load subscription plans');
    }
  };

  const fetchOneTimeItems = async () => {
    try {
      const response = await api.get('/subscriptions/one-time-items');
      setOneTimeItems(response.data);
    } catch (error) {
      toast.error('Failed to load items');
    }
  };

  const fetchActiveSubscription = async () => {
    try {
      const response = await api.get('/subscriptions/active');
      if (response.data.has_subscription) {
        setActiveSubscription(response.data.subscription);
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  };

  const handleSelectPlan = async (tier, billingCycle, amount) => {
    setLoading(true);
    try {
      const response = await api.post('/payments/create-checkout', {
        amount,
        payment_type: 'subscription',
        payment_method: 'telebirr',
        subject: `${tier.charAt(0).toUpperCase() + tier.slice(1)} Subscription - ${billingCycle}`,
        tier,
        billing_cycle: billingCycle
      });

      if (response.data.success) {
        window.location.href = response.data.payment_url;
      } else {
        toast.error('Failed to create payment');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payment failed');
    } finally {
      setLoading(false);
    }
  };

  const handleBuyItem = async (item) => {
    setLoading(true);
    try {
      const response = await api.post('/payments/create-checkout', {
        amount: item.price,
        payment_type: 'one_time',
        payment_method: 'telebirr',
        subject: item.name,
        item_id: item.id
      });

      if (response.data.success) {
        window.location.href = response.data.payment_url;
      } else {
        toast.error('Failed to create payment');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payment failed');
    } finally {
      setLoading(false);
    }
  };

  const getTierIcon = (tier) => {
    if (tier === 'vip') return <Crown className="w-6 h-6 text-[#6B4C9A]" />;
    if (tier === 'premium') return <Star className="w-6 h-6 text-[#FF6B6B]" />;
    return <Zap className="w-6 h-6 text-[#FF6B6B]" />;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 via-white to-pink-50 p-6 pb-24" data-testid="subscription-page">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent" data-testid="subscription-title">Upgrade Your Experience</h1>
          <p className="text-slate-600 mt-2">Choose a plan that works for you</p>
        </div>

        {activeSubscription && (
          <Card className="mb-8 bg-gradient-to-r from-electric-coral/10 to-deep-violet/10 border-electric-coral" data-testid="active-subscription-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-lg">Active Subscription</h3>
                  <p className="text-slate-600">
                    {activeSubscription.tier.toUpperCase()} - {activeSubscription.billing_cycle}
                  </p>
                  <p className="text-sm text-slate-500 mt-1">
                    Renews on {new Date(activeSubscription.current_period_end).toLocaleDateString()}
                  </p>
                </div>
                <Badge className="bg-green-500">Active</Badge>
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="subscriptions" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="subscriptions" data-testid="subscriptions-tab">Subscriptions</TabsTrigger>
            <TabsTrigger value="one-time" data-testid="one-time-tab">Boosts & Super Likes</TabsTrigger>
          </TabsList>

          <TabsContent value="subscriptions">
            <div className="flex justify-center gap-4 mb-6">
              {['monthly', 'quarterly', 'yearly'].map((cycle) => (
                <Button
                  key={cycle}
                  variant={billingCycle === cycle ? 'default' : 'outline'}
                  onClick={() => setBillingCycle(cycle)}
                  className="rounded-full"
                  data-testid={`billing-cycle-${cycle}`}
                >
                  {cycle.charAt(0).toUpperCase() + cycle.slice(1)}
                  {cycle === 'yearly' && <Badge className="ml-2 bg-green-500">Save 20%</Badge>}
                </Button>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {Object.entries(plans).map(([tier, plan]) => (
                <Card
                  key={tier}
                  className={`relative ${
                    plan.popular ? 'border-electric-coral border-2 shadow-lg' : ''
                  } rounded-2xl overflow-hidden`}
                  data-testid={`plan-card-${tier}`}
                >
                  {plan.popular && (
                    <div className="absolute top-0 right-0 bg-gradient-to-r from-electric-coral to-deep-violet text-white px-4 py-1 text-sm font-semibold rounded-bl-lg">
                      Most Popular
                    </div>
                  )}
                  <CardHeader className="text-center">
                    <div className="flex justify-center mb-2">{getTierIcon(tier)}</div>
                    <CardTitle className="font-heading text-2xl">{plan.name}</CardTitle>
                    <CardDescription>{plan.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="text-center">
                      <div className="text-4xl font-bold text-slate-900">
                        {plan.pricing[billingCycle]} ETB
                      </div>
                      <div className="text-sm text-slate-500">
                        per {billingCycle === 'monthly' ? 'month' : billingCycle === 'quarterly' ? 'quarter' : 'year'}
                      </div>
                    </div>

                    <ul className="space-y-3">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-slate-700">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <Button
                      className="w-full rounded-full bg-gradient-to-r from-electric-coral to-deep-violet hover:scale-105 transition-all duration-300"
                      onClick={() => handleSelectPlan(tier, billingCycle, plan.pricing[billingCycle])}
                      disabled={loading}
                      data-testid={`select-plan-${tier}`}
                    >
                      {loading ? 'Processing...' : 'Select Plan'}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="one-time">
            <div className="space-y-8">
              {Object.entries(oneTimeItems).map(([category, items]) => (
                <div key={category}>
                  <h3 className="text-xl font-semibold mb-4 capitalize">{category.replace('_', ' ')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {items.map((item) => (
                      <Card key={item.id} className="rounded-2xl" data-testid={`item-card-${item.id}`}>
                        <CardContent className="p-6 text-center space-y-4">
                          <div className="text-3xl font-bold text-electric-coral">{item.name}</div>
                          <div className="text-2xl font-bold text-slate-900">{item.price} ETB</div>
                          <Button
                            className="w-full rounded-full"
                            onClick={() => handleBuyItem(item)}
                            disabled={loading}
                            data-testid={`buy-item-${item.id}`}
                          >
                            {loading ? 'Processing...' : 'Buy Now'}
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        <div className="mt-12 p-6 bg-slate-50 rounded-2xl">
          <h3 className="font-semibold text-lg mb-4">Payment Methods</h3>
          <div className="flex gap-4">
            <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg border">
              <div className="w-10 h-10 bg-gradient-to-br from-electric-coral to-deep-violet rounded"></div>
              <span className="font-medium">Telebirr</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};